import React, { useState, useEffect, useMemo } from 'react';
import { 
  Select, 
  Input, 
  Button, 
  Card, 
  Row, 
  Col, 
  Badge, 
  Empty,
  Tooltip,
  message,
  Spin
} from 'antd';
import { 
  SearchOutlined, 
  DownloadOutlined, 
  EyeOutlined, 
  FilterOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  DatabaseOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileOutlined
} from '@ant-design/icons';
import { FileDto, DocumentType, DOCUMENT_TYPE_LABELS, getDocumentTypeOptions, FileType } from '../../../types/file.types';
import FileService from '../../../services/file/file.service';
import { getFileUrl } from '../../../utils/file.utils';
import { formatFileSize } from '../../../utils/file.utils';
import dayjs from 'dayjs';

const DocumentsPage: React.FC = () => {
  const [documents, setDocuments] = useState<FileDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | 'all'>('all');
  const [selectedUser, setSelectedUser] = useState<number | 'all'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Belgeleri yükle
  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await FileService.getInstance().getFiles();
      const documentsData = (response as any)?.data || response || [];
      const documents = documentsData.filter((doc: FileDto) => doc.type === FileType.DOCUMENT);
      setDocuments(Array.isArray(documents) ? documents : []);
    } catch (error) {
      message.error('Belgeler yüklenirken hata oluştu');
      console.error('Error fetching documents:', error);
      setDocuments([]); // Hata durumunda boş array
    } finally {
      setLoading(false);
    }
  };

  // Dosya ikonu belirleme
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FilePdfOutlined className="text-danger fs-2" />;
      case 'doc':
      case 'docx':
        return <FileWordOutlined className="text-primary fs-2" />;
      case 'xls':
      case 'xlsx':
        return <FileExcelOutlined className="text-success fs-2" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FileImageOutlined className="text-warning fs-2" />;
      default:
        return <FileOutlined className="text-secondary fs-2" />;
    }
  };

  // Belge tipi badge rengini belirleme
  const getDocumentTypeBadgeColor = (type: DocumentType) => {
    const colors: Record<DocumentType, string> = {
      [DocumentType.ID_CARD]: 'processing',
      [DocumentType.ID_PHOTO]: 'success',
      [DocumentType.PASSPORT]: 'purple',
      [DocumentType.DRIVER_LICENSE]: 'cyan',
      [DocumentType.DIPLOMA]: 'gold',
      [DocumentType.CERTIFICATE]: 'lime',
      [DocumentType.HEALTH_REPORT]: 'red',
      [DocumentType.CRIMINAL_RECORD]: 'orange',
      [DocumentType.RESIDENCE]: 'blue',
      [DocumentType.SGK_ENTRY_DOCUMENT]: 'green',
      [DocumentType.EMPLOYMENT_CONTRACT]: 'yellow',
      [DocumentType.MILITARY_SERVICE_CERTIFICATE]: 'pink'
    };
    return colors[type] || 'default';
  };

  // Filtrelenmiş belgeler
  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter(doc => 
        doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (doc.user?.firstName + ' ' + doc.user?.lastName).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Belge tipi filtresi
    if (selectedDocumentType !== 'all') {
      filtered = filtered.filter(doc => doc.documentType === selectedDocumentType);
    }

    // Kullanıcı filtresi
    if (selectedUser !== 'all') {
      filtered = filtered.filter(doc => doc.user?.id === selectedUser);
    }

    return filtered;
  }, [documents, searchTerm, selectedDocumentType, selectedUser]);

  // Benzersiz kullanıcıları al
  const uniqueUsers = useMemo(() => {
    const users = documents
      .filter(doc => doc.user)
      .map(doc => doc.user!)
      .filter((user, index, self) => 
        index === self.findIndex(u => u.id === user.id)
      );
    return users;
  }, [documents]);

  // İstatistikler
  const statistics = useMemo(() => {
    const totalSize = documents.reduce((sum, doc) => sum + doc.size, 0);
    const documentsByType = documents.reduce((acc, doc) => {
      if (doc.documentType) {
        acc[doc.documentType] = (acc[doc.documentType] || 0) + 1;
      }
      return acc;
    }, {} as Record<DocumentType, number>);

    return {
      totalDocuments: documents.length,
      totalSize,
      documentsByType,
      uniqueUsersCount: uniqueUsers.length,
      recentDocuments: documents.filter(doc => 
        dayjs(doc.createdAt).isAfter(dayjs().subtract(7, 'day'))
      ).length
    };
  }, [documents, uniqueUsers]);

  // Dosya indirme
  const handleDownload = (doc: FileDto) => {
    const link = document.createElement('a');
    link.href = getFileUrl(doc.path);
    link.download = doc.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Dosya görüntüleme
  const handleView = (doc: FileDto) => {
    window.open(getFileUrl(doc.path), '_blank');
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDocumentType('all');
    setSelectedUser('all');
  };

  // Belge kartı bileşeni
  const DocumentCard = ({ document }: { document: FileDto }) => (
    <Card
      className="document-card h-100 shadow-sm hover-shadow-lg"
      bodyStyle={{ padding: '16px' }}
      actions={[
        <Tooltip title="Görüntüle">
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            onClick={() => handleView(document)}
          />
        </Tooltip>,
        <Tooltip title="İndir">
          <Button 
            type="text" 
            icon={<DownloadOutlined />} 
            onClick={() => handleDownload(document)}
          />
        </Tooltip>
      ]}
    >
      <div className="d-flex flex-column h-100">
        {/* Belge tipi badge */}
        {document.documentType && (
          <div className="mb-2">
            <Badge 
              color={getDocumentTypeBadgeColor(document.documentType)} 
              text={DOCUMENT_TYPE_LABELS[document.documentType]}
              size="small"
            />
          </div>
        )}
        
        {/* Dosya ikonu ve ismi */}
        <div className="text-center mb-3">
          {getFileIcon(document.name)}
          <h6 className="mt-2 mb-0 text-truncate" title={document.name}>
            {document.name}
          </h6>
        </div>
        
        {/* Kullanıcı bilgisi */}
        <div className="document-info mt-auto">
          <div className="d-flex align-items-center mb-2">
            <UserOutlined className="me-2 text-muted" />
            <span className="small text-muted">
              {document.user ? `${document.user.firstName} ${document.user.lastName}` : 'Bilinmeyen'}
            </span>
          </div>
          
          <div className="d-flex align-items-center mb-2">
            <CalendarOutlined className="me-2 text-muted" />
            <span className="small text-muted">
              {dayjs(document.createdAt).format('DD.MM.YYYY')}
            </span>
          </div>
          
          <div className="d-flex align-items-center">
            <DatabaseOutlined className="me-2 text-muted" />
            <span className="small text-muted">
              {formatFileSize(document.size)}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );

  // Liste görünümü bileşeni
  const DocumentListItem = ({ document }: { document: FileDto }) => (
    <Card className="document-list-item mb-3 shadow-sm">
      <Row align="middle" gutter={[16, 16]}>
        <Col xs={24} sm={8} md={6} lg={4}>
          <div className="d-flex align-items-center">
            {getFileIcon(document.name)}
            <div className="ms-3">
              <h6 className="mb-0 text-truncate" title={document.name}>
                {document.name}
              </h6>
              {document.documentType && (
                <Badge 
                  color={getDocumentTypeBadgeColor(document.documentType)} 
                  text={DOCUMENT_TYPE_LABELS[document.documentType]}
                  size="small"
                />
              )}
            </div>
          </div>
        </Col>
        
        <Col xs={24} sm={6} md={5} lg={4}>
          <div className="d-flex align-items-center">
            <UserOutlined className="me-2 text-muted" />
            <span className="small">
              {document.user ? `${document.user.firstName} ${document.user.lastName}` : 'Bilinmeyen'}
            </span>
          </div>
        </Col>
        
        <Col xs={12} sm={5} md={4} lg={3}>
          <div className="d-flex align-items-center">
            <CalendarOutlined className="me-2 text-muted" />
            <span className="small">
              {dayjs(document.createdAt).format('DD.MM.YYYY')}
            </span>
          </div>
        </Col>
        
        <Col xs={12} sm={5} md={4} lg={3}>
          <div className="d-flex align-items-center">
            <DatabaseOutlined className="me-2 text-muted" />
            <span className="small">
              {formatFileSize(document.size)}
            </span>
          </div>
        </Col>
        
        <Col xs={24} sm={24} md={5} lg={4}>
          <div className="d-flex justify-content-end gap-2">
            <Tooltip title="Görüntüle">
              <Button 
                type="text" 
                icon={<EyeOutlined />} 
                onClick={() => handleView(document)}
              />
            </Tooltip>
            <Tooltip title="İndir">
              <Button 
                type="text" 
                icon={<DownloadOutlined />} 
                onClick={() => handleDownload(document)}
              />
            </Tooltip>
          </div>
        </Col>
      </Row>
    </Card>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Sayfa başlığı */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-sm-12">
              <div className="page-sub-header">
                <h3 className="page-title">
                  <FileTextOutlined className="me-2" />
                  Belgeler
                </h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="index.html">Raporlar</a>
                  </li>
                  <li className="breadcrumb-item active">Belgeler</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* İstatistikler */}
        {/* <Row gutter={[16, 16]} className="mb-4">
          <Col xs={12} sm={6} md={6} lg={6}>
            <Card>
              <Statistic
                title="Toplam Belge"
                value={statistics.totalDocuments}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6}>
            <Card>
              <Statistic
                title="Kullanıcı Sayısı"
                value={statistics.uniqueUsersCount}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6}>
            <Card>
              <Statistic
                title="Bu Hafta"
                value={statistics.recentDocuments}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={6}>
            <Card>
              <Statistic
                title="Belge Türü"
                value={Object.keys(statistics.documentsByType).length}
                prefix={<FileOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row> */}

        {/* Filtreler */}
        <Card className="mb-4">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input
                placeholder="Belge veya kullanıcı ara..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
              />
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Belge Türü"
                value={selectedDocumentType}
                onChange={setSelectedDocumentType}
                style={{ width: '100%' }}
                allowClear
              >
                <Select.Option value="all">Tüm Belgeler</Select.Option>
                {getDocumentTypeOptions().map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Kullanıcı Seç"
                value={selectedUser}
                onChange={setSelectedUser}
                style={{ width: '100%' }}
                allowClear
              >
                <Select.Option value="all">Tüm Kullanıcılar</Select.Option>
                {uniqueUsers.map(user => (
                  <Select.Option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            
            <Col xs={24} sm={12} md={24} lg={6}>
              <div className="d-flex gap-2">
                <Button 
                  onClick={clearFilters}
                  icon={<FilterOutlined />}
                >
                  Temizle
                </Button>
                <Button.Group>
                  <Button 
                    type={viewMode === 'grid' ? 'primary' : 'default'}
                    onClick={() => setViewMode('grid')}
                  >
                    Kart
                  </Button>
                  <Button 
                    type={viewMode === 'list' ? 'primary' : 'default'}
                    onClick={() => setViewMode('list')}
                  >
                    Liste
                  </Button>
                </Button.Group>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Belge listesi */}
        <Card>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              {filteredDocuments.length} belge bulundu
            </h5>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spin size="large" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Belge bulunamadı"
            />
          ) : (
            <>
              {viewMode === 'grid' ? (
                <Row gutter={[16, 16]}>
                  {filteredDocuments.map(document => (
                    <Col key={document.id} xs={24} sm={12} md={8} lg={6} xl={4}>
                      <DocumentCard document={document} />
                    </Col>
                  ))}
                </Row>
              ) : (
                <div>
                  {filteredDocuments.map(document => (
                    <DocumentListItem key={document.id} document={document} />
                  ))}
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Stil tanımlamaları */}
      <style>
        {`
          .document-card {
            transition: all 0.3s ease;
            border-radius: 8px;
          }
          .document-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
          }
          .document-list-item {
            transition: all 0.2s ease;
            border-radius: 8px;
          }
          .document-list-item:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
          }
          .hover-shadow-lg {
            transition: all 0.3s ease;
          }
          .hover-shadow-lg:hover {
            transform: translateY(-3px);
            box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.1) !important;
          }
          .document-info {
            font-size: 0.85rem;
          }
          .ant-statistic-content {
            font-size: 1.2rem;
          }
          .ant-card-actions {
            background: #fafafa;
          }
          .ant-card-actions > li {
            margin: 4px 0;
          }
        `}
      </style>
    </div>
  );
};

export default DocumentsPage; 